import { useEffect, useRef, useState } from 'react'
import Atmosphere from './components/Atmosphere.jsx'
import { FlowMark } from './components/icons.jsx'
import { DemoTag } from './components/ui.jsx'
import { resolveDataAdapter } from './data/adapter.js'
import { emptySession } from './session.js'
import { tenantFromLocation } from './qrPayload.js'
import { loadDay } from './data/day.js'
import { todayISO } from './data/workdate.js'
import EntryPoint from './screens/EntryPoint.jsx'
import JoinTeam from './screens/JoinTeam.jsx'
import Identity from './screens/Identity.jsx'
import CheckIn from './screens/CheckIn.jsx'
import Home from './screens/Home.jsx'
import Workday from './screens/Workday.jsx'
import ReCheckIn from './screens/ReCheckIn.jsx'
import Owner from './screens/Owner.jsx'
import DailyReport from './screens/DailyReport.jsx'
import WorkdayReview from './screens/WorkdayReview.jsx'

/*
 * BUILD 001 — development-only entry to the Owner / Superintendent surface.
 *
 * `?dev=owner` and nothing else. There is no authentication anywhere in Flow
 * yet, so this is a convenience for fixture testing, NOT a security boundary,
 * and it is deliberately not linked from any worker screen. The flag stays in
 * the URL so a reload keeps the board; "Exit to Flow" clears it.
 */
function ownerRequested() {
  try { return new URLSearchParams(window.location.search).get('dev') === 'owner' }
  catch { return false }
}

export default function App() {
  const [boot, setBoot] = useState({ loading: true, adapter: null, isDemo: false })
  const [step, setStep] = useState('entry')
  const [session, setSession] = useState(emptySession())
  const [autoPhoto, setAutoPhoto] = useState(false)
  const [reviewDay, setReviewDay] = useState(null)

  useEffect(() => {
    let alive = true
    resolveDataAdapter().then(async ({ adapter, isDemo, mode, reason }) => {
      if (!alive) return
      // eslint-disable-next-line no-console
      console.info(`[flow] data mode: ${mode} (${reason})`)

      if (ownerRequested()) {
        setStep('owner')
        setBoot({ loading: false, adapter, isDemo })
        return
      }

      // FD-001: an in-flight day on this device resumes directly — the person
      // is mid-lifecycle (working / checked out), not starting over.
      // BUILD 001: a resumed day lands on Employee Home; WORKDAY opens the
      // Workday screen (Figma 4:2) from there.
      const day = loadDay()
      if (day) {
        setSession((s) => ({ ...s, ...day.session }))
        setStep('home')
        setBoot({ loading: false, adapter, isDemo })
        return
      }

      // QR deep link (?t=<tenant-id>): the printed trailer QR is a link so the
      // native phone camera opens Flow directly. Resolve the tenant and land
      // on Identity — "Arrival, Not Authentication". On failure, fall through
      // to the normal entry flow; a bad link never strands anyone.
      const t = tenantFromLocation()
      if (t) {
        window.history.replaceState(null, '', window.location.pathname)
        try {
          const tenant = await adapter.resolveTenant(t)
          if (!alive) return
          setSession((s) => ({ ...s, tenant }))
          setStep('identity')
        } catch { /* unknown tenant in link — start at entry as usual */ }
      }

      setBoot({ loading: false, adapter, isDemo })
    })
    return () => { alive = false }
  }, [])

  /*
   * State E — Workday Review Required (Figma 9:2).
   *
   * A previous day that Flow recorded a check-in for and never saw end. It is
   * raised ONCE per session, as soon as the person is known: raising it on
   * every return to Home would trap them in a loop, and State E is escapable
   * by design (its back button goes Home). A day the worker has already given
   * their account of is not raised again — it is waiting on a reviewer, not on
   * them.
   */
  const reviewCheckedRef = useRef(false)
  const employeeId = session.employee?.id ?? null
  useEffect(() => {
    if (!boot.adapter || !employeeId || reviewCheckedRef.current) return
    if (typeof boot.adapter.listWorkdaysNeedingReview !== 'function') return
    reviewCheckedRef.current = true
    boot.adapter.listWorkdaysNeedingReview(employeeId, todayISO())
      .then((list) => {
        const pending = (list ?? []).filter((d) => !d.employeeReport)
        if (pending.length) { setReviewDay(pending[0]); setStep('review') }
      })
      .catch(() => { /* never block the worker on this */ })
  }, [boot.adapter, employeeId])

  const patch = (fields) => setSession((s) => ({ ...s, ...fields }))

  function restart() {
    setSession(emptySession())
    setStep('entry')
  }

  function exitOwner() {
    try { window.history.replaceState(null, '', window.location.pathname) } catch { /* noop */ }
    restart()
  }

  // One Active Workday guard, device side: while THIS device holds an active
  // working day, every path back into the flow returns the person to their
  // session — closing the restart() hole that allowed the observed duplicate.
  // A checked-out or closed day does not block: a genuinely new check-in after
  // an explicit check-out is a legitimate new workday (and now goes through
  // State B, which asks why).
  const inDayFlow = step === 'workday' || step === 'home' || step === 'checkin'
    || step === 'recheckin' || step === 'owner' || step === 'report' || step === 'review'
  const activeDay = !boot.loading && !inDayFlow ? loadDay() : null
  const returnToSession = activeDay?.status === 'working'

  /*
   * Deliberate re-check-in after an EXPLICIT check-out. State B (Figma 9:58)
   * asks WHY first — the Locked Founder Decision — and the original check-out
   * is never touched. The reason travels on the session and is recorded on the
   * new segment.
   */
  function startReCheckIn() {
    const d = loadDay()
    if (!d?.session?.employee) { restart(); return }
    setSession((s) => ({
      ...s,
      tenant: d.session.tenant,
      employee: d.session.employee,
      confidence: d.session.confidence,
    }))
    setStep('recheckin')
  }

  /*
   * BUILD 001 (S6) — a re-check-in confirms the project again. The reason is
   * captured on State B; this hands off to the ordinary Check-In screen, which
   * prefills today's assignment and leaves the list open to change. The
   * assignment itself is never rewritten — what is chosen is the REPORTED
   * project of a NEW segment.
   */
  function reasonChosen(reason) {
    const d = loadDay()
    setSession({
      ...emptySession(),
      tenant: d?.session?.tenant ?? session.tenant,
      employee: d?.session?.employee ?? session.employee,
      confidence: d?.session?.confidence ?? session.confidence,
      reCheckInReason: reason,
    })
    setStep('checkin')
  }

  return (
    <>
      {step !== 'owner' && <Atmosphere />}
      {boot.isDemo && step !== 'owner' && <div className="demo-banner"><DemoTag /></div>}

      {boot.loading ? (
        <Splash />
      ) : step === 'owner' ? (
        <Owner adapter={boot.adapter} tenantId={session.tenant?.id ?? null} onExit={exitOwner} />
      ) : returnToSession ? (
        <Home adapter={boot.adapter} session={session} day={loadDay()}
              onWorkday={() => setStep('workday')}
              onDailyReport={() => { setAutoPhoto(false); setStep('report') }}
              onTakePhoto={() => { setAutoPhoto(true); setStep('report') }} />
      ) : step === 'entry' ? (
        /*
         * onOwner is the same destination ?dev=owner reaches; EntryPoint only
         * offers it while the fixture lock is on. The query-param route still
         * works and is still the one a desktop tester will use — the card
         * exists because the artifact host does not pass query strings through.
         */
        <EntryPoint
          onTeamMember={() => setStep('join')}
          onOwner={() => setStep('owner')}
        />
      ) : step === 'join' ? (
        <JoinTeam
          adapter={boot.adapter}
          isDemo={boot.isDemo}
          onBack={() => setStep('entry')}
          onTenant={(tenant) => { patch({ tenant }); setStep('identity') }}
        />
      ) : step === 'identity' ? (
        <Identity
          adapter={boot.adapter}
          tenant={session.tenant}
          onBack={() => setStep('join')}
          onIdentity={(who) => { patch(who); setStep('home') }}
        />
      ) : step === 'home' ? (
        <Home
          adapter={boot.adapter}
          session={session}
          day={loadDay()}
          onWorkday={() => setStep(loadDay() ? 'workday' : 'checkin')}
          onDailyReport={() => { setAutoPhoto(false); setStep('report') }}
          onTakePhoto={() => { setAutoPhoto(true); setStep('report') }}
        />
      ) : step === 'checkin' ? (
        <CheckIn
          adapter={boot.adapter}
          session={session}
          onDone={() => setStep('workday')}
        />
      ) : step === 'review' && reviewDay ? (
        <WorkdayReview
          adapter={boot.adapter}
          session={session}
          day={reviewDay}
          onHome={() => setStep('home')}
        />
      ) : step === 'report' ? (
        <DailyReport
          adapter={boot.adapter}
          session={session}
          autoPhoto={autoPhoto}
          onHome={() => setStep('home')}
        />
      ) : step === 'recheckin' ? (
        <ReCheckIn
          adapter={boot.adapter}
          session={session}
          onHome={() => setStep('home')}
          onReason={reasonChosen}
        />
      ) : (
        <Workday
          adapter={boot.adapter}
          session={session}
          onHome={() => setStep('home')}
          onCheckInAgain={startReCheckIn}
        />
      )}
    </>
  )
}

function Splash() {
  return (
    <div className="screen">
      <div className="screen__body" style={{ justifyContent: 'center', alignItems: 'flex-start' }}>
        <span className="wordmark rise"><FlowMark /> Flow</span>
      </div>
    </div>
  )
}
